// src/hooks/useChallengeForm.js

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { serverTimestamp } from "firebase/firestore";
import {
  createChallengeInFirestore,
  updateChallengeInFirestore,
} from "../services/challengeService";
import { toISOStringMidnightUTC } from "../utils/utils";
import { handleFormChange } from "../handlers/handlers";
import useValidation from "../hooks/useValidation";

const useChallengeForm = (
  challengeId,
  challenge,
  creatorInfo,
  hasSubmissions
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { errors, validate, getErrorMessage, clearErrors, setErrors } =
    useValidation();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    payout: "",
    payoutRange: { min: "", max: "" },
    prize: "",
    startDate: "",
    endDate: "",
    fulfillmentDate: "",
    fanClubOnly: false,
    submissionFee: 0,
    chargeSubmissionFee: false,
    allowCrowdFunding: false,
    editions: { min: 1, max: 1 },
    allowBuys: true,
    hero: null,
  });
  const [rules, setRules] = useState([]);
  const [currentRule, setCurrentRule] = useState("");
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [published, setPublished] = useState(false);
  const [isDraftSaved, setIsDraftSaved] = useState(false); // New state to track if the draft has been saved
  const [workingChallengeId, setWorkingChallengeId] = useState("");

  // Function to reset form data
  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      payout: "",
      payoutRange: { min: "", max: "" },
      prize: "",
      startDate: "",
      endDate: "",
      fulfillmentDate: "",
      fanClubOnly: false,
      submissionFee: 0,
      chargeSubmissionFee: false,
      allowCrowdFunding: false,
      editions: { min: 1, max: 1 },
      allowBuys: true,
      hero: null,
    });
    setRules([]);
    clearErrors();
  };

  // Effect to initialize form data when editing or creating a new challenge
  useEffect(() => {
    if (challengeId && challenge) {
      setWorkingChallengeId(challengeId);
      const parseDate = (dateStr) =>
        dateStr ? new Date(dateStr).toISOString().split("T")[0] : "";

      setFormData((prevData) => ({
        ...prevData,
        title: challenge.title || "",
        description: challenge.description || "",
        prize: challenge.prize || "",
        hero: challenge.hero || null,
        startDate: parseDate(challenge.startDate),
        endDate: parseDate(challenge.endDate),
        fulfillmentDate: parseDate(challenge.fulfillmentDate),
        chargeSubmissionFee: challenge.chargeSubmissionFee,
        allowCrowdFunding: challenge.allowCrowdFunding,
        submissionFee: challenge.submissionFee,
      }));
      setRules(challenge.rules || []);
      setPublished(challenge.published);
      setIsDraftSaved(true); // Set to true since we're editing an existing challenge
    } else if (!challengeId && location.pathname === "/challenge/create") {
      resetForm();
      setIsDraftSaved(false); // Reset draft saved state when creating a new challenge
    }
    // resetForm only writes literal defaults and reads no state, so the
    // identity it gets on each render is irrelevant here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeId, challenge, location.pathname]);

  // Form handlers
  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({ ...prev, fanClubOnly: e.target.checked }));
  };

  const handleToggleChange = () => {
    setFormData((prev) => ({
      ...prev,
      chargeSubmissionFee: !prev.chargeSubmissionFee,
      submissionFee: !prev.chargeSubmissionFee ? "" : 0,
    }));
  };

  const handleCrowdFundingToggleChange = () => {
    setFormData((prev) => ({
      ...prev,
      allowCrowdFunding: !prev.allowCrowdFunding,
    }));
  };  

  const toggleAdvancedSettings = () => {
    setShowAdvancedSettings(!showAdvancedSettings);
  };

  const handleAddRule = () => {
    if (currentRule.trim() !== "") {
      setRules((prevRules) => [...prevRules, currentRule]);
      setCurrentRule("");
    }
  };

  const handleDeleteRule = (index) => {
    setRules((prevRules) => prevRules.filter((_, i) => i !== index));
  };

  const handleImageUpload = (uploadedUrl) => {
    handleChange("hero", uploadedUrl);
  };

  const handleChange = (eOrField, value) => {
    if (eOrField && typeof eOrField === "object" && eOrField.target) {
      handleFormChange(eOrField, formData, setFormData, setErrors);
      clearErrors();
    } else if (typeof eOrField === "string") {
      setFormData((prev) => ({
        ...prev,
        [eOrField]: value,
      }));
      clearErrors();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveChallenge(true); // Explicitly save as published
  };

  const handleSaveAsDraft = async () => {
    await saveChallenge(false); // Explicitly save as a draft
  };

  const saveChallenge = async (isPublished) => {
    if (hasSubmissions) return;

    console.log("Attempting to save challenge:", {
      workingChallengeId,
      isDraftSaved,
    });

    if (isPublished) {
      console.log("isPublished BEFORE validation: ", isPublished);
      const fieldErrors = validate(formData, true);
      if (Object.keys(fieldErrors).length > 0) {
        setSnackbarMessage(getErrorMessage(fieldErrors));
        setShowSnackbar(true);
        return;
      }
      console.log("isPublished AFTER validation: ", isPublished);
      navigate(`/challenge/${workingChallengeId}`);
    }

    try {
      const startDateISO = formData.startDate
        ? toISOStringMidnightUTC(formData.startDate)
        : null;
      const endDateISO = formData.endDate
        ? toISOStringMidnightUTC(formData.endDate)
        : null;
      const fulfillmentDateISO = formData.fulfillmentDate
        ? toISOStringMidnightUTC(formData.fulfillmentDate)
        : null;

      let challengeData = {
        title: formData.title,
        description: formData.description,
        startDate: startDateISO,
        endDate: endDateISO,
        fulfillmentDate: fulfillmentDateISO,
        payout: 0,
        editions: 1,
        hero: formData.hero,
        rules: rules,
        createdAt: serverTimestamp(),
        prize: formData.prize,
        chargeSubmissionFee: formData.chargeSubmissionFee,
        submissionFee: formData.submissionFee,
        allowCrowdFunding: formData.allowCrowdFunding,
        published: isPublished,
        creatorInfo,
      };

      challengeData = Object.fromEntries(
        Object.entries(challengeData).filter(([_, v]) => v !== undefined)
      );

      if (challengeId || isDraftSaved) {
        // Update the existing draft if challengeId is present or the draft has been saved before
        if (!challengeId) {
          console.error("Challenge ID is undefined, cannot update challenge.");
          setSnackbarMessage(
            "Unable to update the challenge. Please try again."
          );
          setShowSnackbar(true);
          return;
        }

        await updateChallengeInFirestore(challengeId, challengeData);
        setPublished(isPublished);
        setSnackbarMessage(
          isPublished
            ? "Challenge updated successfully!"
            : "Draft updated successfully."
        );
        setShowSnackbar(true);
        if (isPublished) {
          navigate(`/challenge/${workingChallengeId}`);
        }
      } else {
        // Create a new challenge if it hasn't been saved as a draft before
        const newChallengeId = await createChallengeInFirestore(challengeData);
        setIsDraftSaved(true); // Mark the draft as saved
        setSnackbarMessage("Challenge created successfully!");
        setShowSnackbar(true);

        // Update challengeId state and change the URL to include the new challengeId
        if (isPublished) {
          console.log('newChallengeId: ', newChallengeId);
          navigate(`/challenge/${newChallengeId}`);
        } else {
          navigate(`/challenge/edit/${newChallengeId}`);
        }
      }
    } catch (error) {
      console.error("Error saving challenge:", error);
      setSnackbarMessage("There was an error saving the challenge.");
      setShowSnackbar(true);
    }
  };

  return {
    formData,
    rules,
    currentRule,
    showAdvancedSettings,
    errors,
    showSnackbar,
    snackbarMessage,
    published,
    isDraftSaved, // Expose the draft saved state
    handleChange,
    handleCheckboxChange,
    handleToggleChange,
    handleCrowdFundingToggleChange,
    toggleAdvancedSettings,
    handleAddRule,
    handleDeleteRule,
    handleImageUpload,
    handleSubmit,
    handleSaveAsDraft,
    setShowSnackbar,
    setCurrentRule,
  };
};

export default useChallengeForm;
